



import { useEffect, useState } from "react";
import {
  getFeedsApi,
  getEmployeesApi,
  deleteFeedApi,
  likeFeedApi,
  unlikeFeedApi,
  addCommentApi,
} from "../../app/api";
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  MoreHorizontal, 
  Send, 
  Trash2,
  Calendar,
  Sparkles,
  Trophy,
  Star
} from "lucide-react";
import CreatePostModal from "./CreatePostModal";
import { toast } from "react-hot-toast";
import moment from "moment";
import { motion } from "framer-motion";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
export default function Feeds({ showModal, setShowModal }) {
 
  const [feeds, setFeeds] = useState([]);
  const [realEmployees, setRealEmployees] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
 
  useEffect(() => {
    loadFeeds();
  }, []);
 
  const loadFeeds = async () => {
    const [res, employeesRes] = await Promise.all([
      getFeedsApi(),
      getEmployeesApi(),
    ]);
    if (res.success) setFeeds(res.data);
    if (employeesRes?.success) setRealEmployees(employeesRes.data || []);
  };

  const getPersonName = (person = {}) =>
    person.name ||
    person.publisher_name ||
    person.meta?.display_name ||
    [person.first_name || person.meta?.first_name, person.last_name || person.meta?.last_name].filter(Boolean).join(" ") ||
    "";

  const getPersonId = (person = {}) =>
    person.id ||
    person.user_id ||
    person.employee_id ||
    person.publisher_user ||
    person.recipient_id ||
    person.meta?.user_id ||
    person.meta?.employee_id;

  const isDemoPerson = (person = {}) => {
    const name = getPersonName(person).trim().toLowerCase();
    const email = String(person.email || person.user_email || person.work_email || person.meta?.email || "").toLowerCase();
    const marker = String(person.source || person.origin || person.user_type || person.employee_type || person.meta?.source || "").toLowerCase();

    return (
      person.is_dummy === true ||
      person.isDummy === true ||
      person.is_demo === true ||
      person.isDemo === true ||
      person.is_test === true ||
      person.isTest === true ||
      /\b(dummy|demo|sample|test|fake)\b/.test(name) ||
      /\b(dummy|demo|sample|test|fake)\b/.test(email) ||
      /\b(dummy|demo|sample|test|fake)\b/.test(marker) ||
      /@(example|test|dummy|demo|sample)\./.test(email)
    );
  };

  const isInactivePerson = (person = {}) => {
    const activeValue = person.is_active ?? person.active ?? person.isActive;
    const deletedValue = person.is_deleted ?? person.deleted ?? person.isDeleted;
    const status = String(person.status || person.employee_status || person.account_status || "").toLowerCase();
    const employmentStatus = String(person.employment_status || person.work_status || "").toLowerCase();

    if (deletedValue === true || Number(deletedValue) === 1) return true;
    if (activeValue === false || Number(activeValue) === 0) return true;
    if (person.deleted_at || person.exit_date || person.last_working_day || person.relieving_date) return true;
    return ["inactive", "deleted", "ex", "ex employee", "terminated", "resigned", "left"].some(
      (value) => status === value || employmentStatus === value
    );
  };

  const realEmployeeLookup = realEmployees.reduce((lookup, employee) => {
    if (isDemoPerson(employee) || isInactivePerson(employee)) return lookup;
    const id = getPersonId(employee);
    const name = getPersonName(employee).trim().toLowerCase();
    if (id !== undefined && id !== null && id !== "") lookup.ids.add(String(id));
    if (name) lookup.names.add(name);
    return lookup;
  }, { ids: new Set(), names: new Set() });

  const isRealFeedPerson = (person = {}) => {
    if (isDemoPerson(person) || isInactivePerson(person)) return false;
    const id = getPersonId(person);
    const name = getPersonName(person).trim().toLowerCase();
    if (id !== undefined && id !== null && id !== "" && realEmployeeLookup.ids.has(String(id))) return true;
    return Boolean(name && realEmployeeLookup.names.has(name));
  };

  const filterRealEmployeeFeed = (feed) => {
    if (!["birthday", "anniversary", "anniversary_post", "new_joiner"].includes(feed.type)) return true;
    if (!realEmployeeLookup.ids.size && !realEmployeeLookup.names.size) return false;

    if (Array.isArray(feed.meta?.users)) {
      feed.meta.users = feed.meta.users.filter(isRealFeedPerson);
      return feed.meta.users.length > 0;
    }

    return isRealFeedPerson(feed);
  };
 
  const handleDelete = async (id) => {
    const res = await deleteFeedApi(id);
    if (res.success) loadFeeds();
  };
 
  const handleLike = async (feed) => {
    // Optimistic update
    const updatedFeeds = feeds.map(f => {
      if (f.id === feed.id) {
        return {
          ...f,
          is_liked: !f.is_liked,
          total_likes: f.is_liked ? (f.total_likes - 1) : (f.total_likes + 1)
        };
      }
      return f;
    });
    setFeeds(updatedFeeds);

    const res = feed.is_liked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);
    if (!res.success) {
      // Rollback if failed
      loadFeeds();
      toast.error("Failed to update like");
    }
  };
 
  const handleComment = async (id, comment) => {
    if (!comment.trim()) return;
    const res = await addCommentApi(id, { comment });
    if (res.success) {
      loadFeeds();
      toast.success("Comment added");
    }
  };

  const handleShare = (feed) => {
    const text = `Check out this post on Uptask by ${feed.publisher_name}:\n\n${feed.meta?.message || ""}\n\n${feed.image ? (IMAGE_BASE_URL + "/" + feed.image) : ""}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank");
  };
 
  return (
 
    <div className="min-h-screen bg-gradient-to-b from-[#0f0c29] via-[#1a0f4c] to-[#09061f] py-10 rounded-[24px]">
 
      <div className="flex justify-center">
 
        <div className="w-full max-w-[800px] px-4 text-white">
 
          <div className="max-h-[700px] overflow-y-auto no-scrollbar pr-2">
 
            {feeds.length === 0 ? (
              <div className="flex justify-center items-center h-[200px]">
                <p className="text-gray-400 text-lg">No posts yet</p>
              </div>
            ) : (
 
              feeds
                .map(f => ({ 
                  ...f, 
                  meta: (typeof f.meta === "string" && f.meta.startsWith("{")) ? JSON.parse(f.meta) : f.meta 
                }))
                .filter(filterRealEmployeeFeed)
                .filter((feed) => {
                  if (feed.type === 'birthday_post') return false;
                  // For event-based posts, only show if event_date is TODAY
                  if (
                    feed.type === 'birthday' ||
                    feed.type === 'anniversary' ||
                    feed.type === 'anniversary_post'
                  ) {
                    if (!feed.event_date) return false;
                    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
                    return feed.event_date === today;
                  }
                  return true;
                })
                .reduce((acc, feed) => {
                  if (feed.type === "anniversary" || feed.type === "anniversary_post") {
                    const date = feed.event_date;
                    // Create a unique key based on date and involved user IDs
                    // If meta.users is missing, use the celebrant's name to ensure uniqueness per person
                    const userIds = (feed.meta?.users?.map(u => u.id) || [feed.publisher_user, feed.meta?.first_name, feed.meta?.last_name]).sort().join(',');
                    const groupKey = `anniv-${date}-${userIds}`;
                    
                    const existingIdx = acc.findIndex(item => item._groupKey === groupKey);
                    
                    if (existingIdx > -1) {
                      // Prioritize 'anniversary_post' over 'anniversary'
                      if (feed.type === "anniversary_post") {
                        acc[existingIdx] = { ...feed, _groupKey: groupKey };
                      }
                      return acc;
                    }
                    
                    acc.push({ ...feed, _groupKey: groupKey });
                    return acc;
                  }
                  acc.push(feed);
                  return acc;
                }, [])
                .map((feed) => (
 
                  feed.type === "birthday" ? (
 
 
 
<div
  key={feed.id}
  className="mb-8 w-full min-h-[362px] rounded-3xl overflow-hidden relative border border-white/10"
>
  {/* BACKGROUND */}
  <div className="absolute inset-0 bg-gradient-to-r from-[#142a7c] via-[#1f3ea8] to-[#0f1c4d]"></div>

  {/* CONTENT */}
  <div className="relative z-10 flex h-full items-center justify-between px-12 pt-10 pb-6">
    {/* LEFT TEXT */}
    <div className="max-w-[320px]">
      <p className="text-white/70 tracking-[6px] text-sm">Happy</p>
      <h1 className="text-[56px] font-extrabold text-white leading-none">BIRTHDAY</h1>
      <p className="text-white/80 text-sm mt-1 tracking-widest">YOUR MOMENT IS HERE</p>
      <p className="mt-4 text-white/80 text-xs leading-relaxed">
        Wishing you a fantastic day filled with happiness, laughter and unforgettable moments.
      </p>
      <h3 className="mt-5 text-cyan-300 text-xl font-semibold">
        {feed.meta?.first_name} {feed.meta?.last_name}
      </h3>

      {/* DATE BADGE */}
      <div className="mt-6 flex items-center gap-3">
        <div className="w-[70px] h-[70px] rounded-full border border-white/40 flex flex-col items-center justify-center text-white">
          <span className="text-xl font-bold">
            {feed.event_date ? new Date(feed.event_date).getDate() : new Date().getDate()}
          </span>
          <span className="text-[10px]">
            {feed.event_date
              ? new Date(feed.event_date).toLocaleString('default', { month: 'short' }).toUpperCase()
              : new Date().toLocaleString('default', { month: 'short' }).toUpperCase()
            }
          </span>
        </div>
        <span className="text-white/70 text-sm">Celebrate today 🎉</span>
      </div>
    </div>

    {/* PROFILE IMAGE */}
    <div className="relative">
      <div className="absolute -inset-3 rounded-full bg-white/20 blur-lg"></div>
      <img
        src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
        alt="profile"
        className="relative w-[180px] h-[180px] object-cover rounded-full border-[10px] border-white/40 shadow-[0_15px_50px_rgba(0,0,0,0.8)]"
      />
    </div>
  </div>

  {/* ACTIONS & STATS */}
  <div className="relative z-10 flex items-center justify-between px-12 py-5 border-t border-white/10 bg-black/20">
    <div className="flex items-center gap-6">
      <button
        onClick={() => handleLike(feed)}
        className={`flex items-center gap-2.5 transition-all duration-300 group
        ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
      >
        <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
        <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
      </button>

      <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
        <MessageCircle size={22} />
        <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
      </div>
    </div>

    <button
      onClick={() => handleShare(feed)}
      className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
    >
      <Share2 size={18} />
      <span className="text-xs font-bold uppercase tracking-wider">Share</span>
    </button>
  </div>

  {/* COMMENTS SECTION */}
  <div className="relative z-10 px-12 pb-8 pt-5 border-t border-white/10 bg-black/30">
    {feed.comments?.length > 0 && (
      <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
        {feed.comments.map((c) => (
          <div key={c.id} className="flex gap-3 group">
            <img 
              src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
              className="w-8 h-8 rounded-full object-cover border border-white/10" 
              alt="" 
            />
            <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
              <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                {c.user?.first_name} {c.user?.last_name}
              </p>
              <p className="text-[13px] text-white/80 leading-snug">
                {c.comment}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
    <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
  </div>
</div>
 
                    ) : (feed.type === "anniversary" || feed.type === "anniversary_post") ? (
                      <div
                        key={feed.id}
                        className="mb-10 w-full min-h-[400px] rounded-[40px] overflow-hidden relative border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.7)] group"
                      >
                        {/* PREMIUM BACKGROUND */}
                        <div className="absolute inset-0 bg-[#0f0c29]">
                          <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] bg-purple-600/30 rounded-full blur-[100px] animate-pulse"></div>
                          <div className="absolute bottom-[-10%] left-[-10%] w-[350px] h-[350px] bg-indigo-600/20 rounded-full blur-[120px]"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f]"></div>
                        </div>

                        {/* DECORATIVE ELEMENTS */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                           <div className="absolute top-10 left-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                           <div className="absolute bottom-10 right-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                           <Sparkles className="absolute top-10 right-10 text-yellow-400/30 animate-twinkle" size={40} />
                        </div>

                        {/* CONTENT */}
                        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-10">
                          
                          <div className="mb-6 px-5 py-1.5 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                            <p className="text-indigo-300 tracking-[8px] text-[10px] font-black uppercase">
                              {feed.meta?.day_type === 'tomorrow' ? "Tomorrow's Anniversary" : "Work Anniversary"}
                            </p>
                          </div>
                          
                          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8 tracking-tighter">
                            {feed.meta?.day_type === 'tomorrow' ? (
                              <>JOINING US<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOMORROW</span></>
                            ) : (feed.meta?.users?.length > 1 || feed.meta?.is_combined) ? (
                              <>CELEBRATING<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOGETHER</span></>
                            ) : (
                              <>YEARS OF<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">EXCELLENCE</span></>
                            )}
                          </h1>

                          {/* AVATARS GROUP */}
                          <div className="flex -space-x-8 mb-10 items-center justify-center translate-x-4">
                            {(feed.meta?.users || []).map((emp, idx) => (
                              <div 
                                key={emp.id} 
                                className="relative transition-all duration-500 hover:scale-110 hover:-translate-y-2 hover:z-30"
                                style={{ zIndex: 10 + idx }}
                              >
                                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40 group-hover:opacity-70 transition-opacity"></div>
                                <img
                                  src={`${IMAGE_BASE_URL}${emp.profile_pic || feed.publisher_profile_pic || '/default-avatar.png'}`}
                                  className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
                                  alt={emp.first_name}
                                />
                                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-lg border border-white/20 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
                                  {emp.first_name} {emp.last_name}
                                </div>
                              </div>
                            ))}
                            
                            {/* If no users in meta (old format or fallback) */}
                            {(!feed.meta?.users || feed.meta.users.length === 0) && (
                               <div className="relative">
                                 <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40"></div>
                                 <img
                                   src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
                                   className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
                                   alt=""
                                 />
                               </div>
                            )}
                          </div>

                          <div className="max-w-[550px] space-y-4">
                            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                              {feed.meta?.users?.length > 1 
                                ? `${feed.meta.users.map(e => e.first_name).join(' & ')}`
                                : `${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}`
                              }
                            </h2>
                            
                            <div className="inline-flex items-center gap-3 px-6 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl border border-white/10">
                               <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                               <p className="text-blue-200 text-lg font-medium italic">
                                 Completed {feed.meta?.users?.[0]?.years_completed || feed.meta?.years_completed || 1} Year milestone
                               </p>
                            </div>

                            <p className="text-white/50 text-sm leading-relaxed font-light px-4">
                              {feed.meta?.message || "Today marks a special milestone in your journey with us. Your dedication and passion have been truly inspiring!"}
                            </p>
                          </div>

                          {/* DATE BADGE */}
                          <div className="mt-12 flex flex-col items-center gap-2">
                             <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
                             <p className="text-[11px] text-white/30 font-bold tracking-[4px] uppercase">
                               {moment(feed.event_date).format("MMMM YYYY")}
                             </p>
                          </div>
                        </div>

                        {/* ACTIONS & STATS */}
                        <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
                          <div className="flex items-center gap-6">
                            <button
                              onClick={() => handleLike(feed)}
                              className={`flex items-center gap-2.5 transition-all duration-300 group
                              ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                            >
                              <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                              <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                            </button>

                            <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                              <MessageCircle size={22} />
                              <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleShare(feed)}
                            className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                          >
                            <Share2 size={18} />
                            <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                          </button>
                        </div>

                        {/* COMMENTS SECTION */}
                        <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
                          {feed.comments?.length > 0 && (
                            <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
                              {feed.comments.map((c) => (
                                <div key={c.id} className="flex gap-3 group">
                                  <img 
                                    src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                    className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                    alt="" 
                                  />
                                  <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                    <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                      {c.user?.first_name} {c.user?.last_name}
                                    </p>
                                    <p className="text-[13px] text-white/80 leading-snug">
                                      {c.comment}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                        </div>
                      </div>
                    ) : feed.type === "new_joiner" ? (
                      (() => {
                        const meta = typeof feed.meta === 'string' ? JSON.parse(feed.meta) : feed.meta;
                        const isFemale = (meta?.gender || '').toLowerCase() === 'female';
                       
                        return (
                          /* 🚀 NEW JOINER BANNER (ULTRA PREMIUM) */
<div
  key={feed.id}
  className="mb-8 w-full min-h-[320px] rounded-3xl overflow-hidden relative border border-white/5"
>
  {/* BACKGROUND */}
  <div className="absolute inset-0 bg-[#0f0c29] overflow-hidden">
    <div className={`absolute top-0 right-0 w-[400px] h-[400px] ${isFemale ? 'bg-pink-600/30' : 'bg-purple-600/30'} rounded-full blur-[100px] -mr-40 -mt-40 animate-pulse`}></div>
    <div className={`absolute bottom-0 left-0 w-[350px] h-[350px] ${isFemale ? 'bg-purple-600/20' : 'bg-blue-600/20'} rounded-full blur-[90px] -ml-30 -mb-30`}></div>
    <div className={`absolute inset-0 bg-gradient-to-br ${isFemale ? 'from-[#4a1a3a] via-[#1a0c2f] to-[#0a081f]' : 'from-[#1e1a5a] via-[#100c2f] to-[#0a081f]'}`}></div>
  </div>
  
  {/* DECOR */}
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full opacity-40 animate-ping"></div>
    <div className={`absolute bottom-20 right-20 w-3 h-3 ${isFemale ? 'bg-pink-400' : 'bg-cyan-400'} rounded-full opacity-30 animate-pulse`}></div>
  </div>
  
  {/* CONTENT */}
  <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-12 pb-6">
  
    {/* TAG */}
    <span className={`mb-4 px-4 py-1 bg-white/10 rounded-full text-[11px] uppercase tracking-[3px] font-bold ${isFemale ? 'text-pink-400' : 'text-cyan-400'} border border-white/10 backdrop-blur-md`}>
      ✨ New Team Member
    </span>
  
    {/* HEADING */}
    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight tracking-tight uppercase">
      WELCOME
      <br />
      <span className={`text-transparent bg-clip-text bg-gradient-to-r ${isFemale ? 'from-white via-pink-300 to-purple-400' : 'from-white via-cyan-300 to-blue-400'}`}>
        TO THE TEAM
      </span>
    </h1>
  
    {/* NAME + ROLE */}
    <div className="mt-6">
      <h2 className="text-2xl md:text-3xl font-bold text-white">
        {meta?.first_name} {meta?.last_name}
      </h2>
  
      <p className="mt-2 text-lg text-white/70 italic">
        {meta?.job_title || "Innovative Team Member"}
      </p>
    </div>
  </div>

  {/* ACTIONS & STATS */}
  <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
    <div className="flex items-center gap-6">
      <button
        onClick={() => handleLike(feed)}
        className={`flex items-center gap-2.5 transition-all duration-300 group
        ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
      >
        <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
        <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
      </button>

      <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
        <MessageCircle size={22} />
        <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
      </div>
    </div>

    <button
      onClick={() => handleShare(feed)}
      className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
    >
      <Share2 size={18} />
      <span className="text-xs font-bold uppercase tracking-wider">Share</span>
    </button>
  </div>

  {/* COMMENTS SECTION */}
  <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
    {feed.comments?.length > 0 && (
      <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
        {feed.comments.map((c) => (
          <div key={c.id} className="flex gap-3 group">
            <img 
              src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
              className="w-8 h-8 rounded-full object-cover border border-white/10" 
              alt="" 
            />
            <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
              <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                {c.user?.first_name} {c.user?.last_name}
              </p>
              <p className="text-[13px] text-white/80 leading-snug">
                {c.comment}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
    <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
  </div>
</div>
                        );
                      })()
                    ) : feed.type === "employee_of_the_month" ? (
                      /* 🏆 EMPLOYEE OF THE MONTH (ULTRA PREMIUM) */
                      <div
                        key={feed.id}
                        className="mb-10 w-full min-h-[420px] rounded-[40px] overflow-hidden relative border border-yellow-500/20 shadow-[0_40px_100px_rgba(0,0,0,0.8)] group"
                      >
                        {/* PREMIUM BACKGROUND */}
                        <div className="absolute inset-0 bg-[#0f0c29]">
                          <div className="absolute top-[-10%] left-[-10%] w-[300px] h-[300px] bg-yellow-600/20 rounded-full blur-[100px] animate-pulse"></div>
                          <div className="absolute bottom-[-10%] right-[-10%] w-[350px] h-[350px] bg-indigo-600/30 rounded-full blur-[120px]"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f]"></div>
                          
                          {/* Animated Particles/Stars */}
                          <div className="absolute inset-0 opacity-30">
                            {[...Array(20)].map((_, i) => (
                              <div 
                                key={i}
                                className="absolute bg-white rounded-full animate-twinkle"
                                style={{
                                  width: Math.random() * 3 + 'px',
                                  height: Math.random() * 3 + 'px',
                                  top: Math.random() * 100 + '%',
                                  left: Math.random() * 100 + '%',
                                  animationDelay: Math.random() * 5 + 's'
                                }}
                              />
                            ))}
                          </div>
                        </div>

                        {/* CONTENT */}
                        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-10">
                          
                          <div className="mb-6 px-6 py-2 bg-yellow-500/10 backdrop-blur-xl rounded-full border border-yellow-500/30 shadow-[0_0_20px_rgba(234,179,8,0.2)]">
                            <p className="text-yellow-400 tracking-[8px] text-[10px] font-black uppercase flex items-center gap-3">
                              <Trophy size={14} className="fill-current" />
                              Employee of the Month
                              <Trophy size={14} className="fill-current" />
                            </p>
                          </div>
                          
                          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-8 tracking-tighter uppercase">
                            OUR STAR<br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500">
                              PERFORMER
                            </span>
                          </h1>

                          {/* WINNER IMAGE */}
                          <div className="relative mb-10 group/img">
                            <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-yellow-400 to-amber-600 blur-xl opacity-40 group-hover/img:opacity-80 transition-all duration-700 animate-pulse"></div>
                            <div className="relative">
                              <img
                                src={`${IMAGE_BASE_URL}${feed.meta?.winnerPic || '/default-avatar.png'}`}
                                className="w-36 h-36 rounded-full border-[8px] border-[#1B103F] object-cover shadow-2xl relative z-10 transition-transform duration-700 group-hover/img:scale-110"
                                alt={feed.meta?.winnerName}
                              />
                              <div className="absolute -right-2 -bottom-2 bg-yellow-500 text-black p-3 rounded-full shadow-xl z-20 animate-bounce">
                                <Star size={24} fill="currentColor" />
                              </div>
                            </div>
                          </div>

                          <div className="max-w-[550px] space-y-4">
                            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                              {feed.meta?.winnerName}
                            </h2>
                            
                            <div className="inline-flex items-center gap-4 px-8 py-3 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 rounded-2xl border border-yellow-500/30 backdrop-blur-md">
                               <p className="text-yellow-200 text-xl font-black italic tracking-wide">
                                 {feed.meta?.monthName} {feed.meta?.year}
                               </p>
                            </div>

                            <p className="text-white/60 text-base leading-relaxed font-medium px-8 mt-4">
                              Outstanding performance, dedication, and excellence! Thank you for your incredible contribution to our success this month.
                            </p>
                          </div>
                        </div>

                        {/* ACTIONS & STATS */}
                        <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
                          <div className="flex items-center gap-6">
                            <button
                              onClick={() => handleLike(feed)}
                              className={`flex items-center gap-2.5 transition-all duration-300 group
                              ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                            >
                              <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                              <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                            </button>

                            <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                              <MessageCircle size={22} />
                              <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleShare(feed)}
                            className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                          >
                            <Share2 size={18} />
                            <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                          </button>
                        </div>

                        {/* COMMENTS SECTION */}
                        <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
                          {feed.comments?.length > 0 && (
                            <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
                              {feed.comments.map((c) => (
                                <div key={c.id} className="flex gap-3 group">
                                  <img 
                                    src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                    className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                    alt="" 
                                  />
                                  <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                    <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                      {c.user?.first_name} {c.user?.last_name}
                                    </p>
                                    <p className="text-[13px] text-white/80 leading-snug">
                                      {c.comment}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                        </div>
                      </div>
                    ) : (
 
                    /* NORMAL POST */
                    <div
                      key={feed.id}
                      className="
                      mb-10 rounded-[32px] bg-gradient-to-br from-[#1e174a]/80 to-[#0f0c29]/90 backdrop-blur-2xl
                      border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.5)]
                      overflow-hidden transition-all duration-300 hover:shadow-[0_35px_80px_rgba(0,0,0,0.7)] hover:translate-y-[-4px]
                      "
                    >
                      {/* HEADER */}
                      <div className="flex items-center justify-between px-7 py-6">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <img
                              src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
                              className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/50 shadow-lg"
                              alt=""
                            />
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#1e174a] rounded-full"></div>
                          </div>
                          <div>
                            <p className="font-bold text-white text-[15px] tracking-wide">
                              {feed.publisher_name}
                            </p>
                            <p className="text-[11px] text-white/40 font-medium uppercase tracking-tighter">
                               {moment(feed.created_at).format("DD MMM YYYY")}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => handleDelete(feed.id)}
                          className="p-2 rounded-full hover:bg-white/5 text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      {/* MESSAGE */}
                      {feed.meta?.message && (
                        <div className="px-7 pb-4">
                          <div className="text-[16px] text-white/90 leading-relaxed break-words font-light">
                            {feed.meta.message.split("\n").map((line, i) => (
                              <span key={i}>
                                {line}
                                <br />
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* IMAGE */}
                      {feed.image && (
                        <div className="px-5 pb-6">
                          <div className="rounded-[24px] overflow-hidden bg-black/40 border border-white/5 shadow-inner">
                            <img
                              src={`${IMAGE_BASE_URL}/${feed.image}`}
                              className="w-full max-h-[600px] object-contain mx-auto transition-transform duration-700 hover:scale-[1.02]"
                              alt=""
                            />
                          </div>
                        </div>
                      )}

                      {/* ACTIONS & STATS */}
                      <div className="flex items-center justify-between px-7 py-5 border-t border-white/5 bg-white/[0.02]">
                        <div className="flex items-center gap-6">
                          <button
                            onClick={() => handleLike(feed)}
                            className={`flex items-center gap-2.5 transition-all duration-300 group
                            ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                          >
                            <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                            <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                          </button>

                          <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                            <MessageCircle size={22} />
                            <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                          </div>
                        </div>

                        <button
                          onClick={() => handleShare(feed)}
                          className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                        >
                          <Share2 size={18} />
                          <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                        </button>
                      </div>

                      {/* COMMENTS SECTION */}
                      <div className="px-7 pb-6 pt-5 border-t border-white/5 bg-black/10">
                        {feed.comments?.length > 0 && (
                          <div className="space-y-4 mb-5">
                            {feed.comments.map((c) => (
                              <div key={c.id} className="flex gap-3 group">
                                <img 
                                  src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                  className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                  alt="" 
                                />
                                <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                  <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                    {c.user?.first_name} {c.user?.last_name}
                                  </p>
                                  <p className="text-[13px] text-white/80 leading-snug">
                                    {c.comment}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                      </div>
                    </div>
                  )
 
                ))
 
            )}
 
          </div>
 
        </div>
 
      </div>
 
      <CreatePostModal
        showModal={showModal}
        setShowModal={setShowModal}
        onPostCreated={loadFeeds}
      />
 
    </div>
  );
}
 
 
/* COMMENT BOX */
function CommentBox({ onSubmit }) {
  const [text, setText] = useState("");

  const handleSend = () => {
    if (!text.trim()) return;
    onSubmit(text);
    setText("");
  };

  return (
    <div className="flex gap-3 mt-4">
      <div className="flex-1 relative group">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          placeholder="Write a comment..."
          className="w-full bg-white/5 border border-white/10 px-5 py-3 rounded-2xl text-[14px] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
        />
      </div>

      <button
        onClick={handleSend}
        disabled={!text.trim()}
        className="bg-cyan-600 p-3 rounded-2xl text-white hover:bg-cyan-500 disabled:opacity-40 disabled:hover:bg-cyan-600 transition-all active:scale-90 flex items-center justify-center shadow-lg shadow-cyan-900/20"
      >
        <Send size={20} />
      </button>
    </div>
  );
}





// import { useEffect, useState } from "react";
// import {
//   getFeedsApi,
//   deleteFeedApi,
//   likeFeedApi,
//   unlikeFeedApi,
//   addCommentApi,
// } from "../../app/api";
// import { 
//   Heart, 
//   MessageCircle, 
//   Share2, 
//   MoreHorizontal, 
//   Send, 
//   Trash2,
//   Calendar,
//   Sparkles
// } from "lucide-react";
// import CreatePostModal from "./CreatePostModal";
// import { toast } from "react-hot-toast";
// import moment from "moment";

// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
// export default function Feeds({ showModal, setShowModal }) {
 
//   const [feeds, setFeeds] = useState([]);
//   const [savedPosts, setSavedPosts] = useState([]);
 
//   useEffect(() => {
//     loadFeeds();
//   }, []);
 
//   const loadFeeds = async () => {
//     const res = await getFeedsApi();
//     if (res.success) setFeeds(res.data);
//   };
 
//   const handleDelete = async (id) => {
//     const res = await deleteFeedApi(id);
//     if (res.success) loadFeeds();
//   };
 
//   const handleLike = async (feed) => {
//     // Optimistic update
//     const updatedFeeds = feeds.map(f => {
//       if (f.id === feed.id) {
//         return {
//           ...f,
//           is_liked: !f.is_liked,
//           total_likes: f.is_liked ? (f.total_likes - 1) : (f.total_likes + 1)
//         };
//       }
//       return f;
//     });
//     setFeeds(updatedFeeds);

//     const res = feed.is_liked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);
//     if (!res.success) {
//       // Rollback if failed
//       loadFeeds();
//       toast.error("Failed to update like");
//     }
//   };
 
//   const handleComment = async (id, comment) => {
//     if (!comment.trim()) return;
//     const res = await addCommentApi(id, { comment });
//     if (res.success) {
//       loadFeeds();
//       toast.success("Comment added");
//     }
//   };

//   const handleShare = (feed) => {
//     const text = `Check out this post on Uptask by ${feed.publisher_name}:\n\n${feed.meta?.message || ""}\n\n${feed.image ? (IMAGE_BASE_URL + "/" + feed.image) : ""}`;
//     const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
//     window.open(whatsappUrl, "_blank");
//   };
 
//   return (
 
//     <div className="min-h-screen bg-gradient-to-b from-[#0f0c29] via-[#1a0f4c] to-[#09061f] py-10 rounded-[24px]">
 
//       <div className="flex justify-center">
 
//         <div className="w-full max-w-[800px] px-4 text-white">
 
//           <div className="max-h-[700px] overflow-y-auto no-scrollbar pr-2">
 
//             {feeds.length === 0 ? (
//               <div className="flex justify-center items-center h-[200px]">
//                 <p className="text-gray-400 text-lg">No posts yet</p>
//               </div>
//             ) : (
 
//               feeds
//                 .filter((feed) => {
//                   if (feed.type === 'birthday_post') return false;
//                   // Hide future anniversaries (only show if today or not specified)
//                   if ((feed.type === 'anniversary' || feed.type === 'anniversary_post') && (feed.meta?.day_type === 'tomorrow' || feed.meta?.day_type === 'upcoming')) {
//                     return false;
//                   }
//                   return true;
//                 })
//                 .reduce((acc, feed) => {
//                   if (feed.type === "anniversary" || feed.type === "anniversary_post") {
//                     const date = feed.event_date;
//                     // Create a unique key based on date and involved user IDs
//                     const userIds = (feed.meta?.users?.map(u => u.id) || [feed.publisher_user]).sort().join(',');
//                     const groupKey = `anniv-${date}-${userIds}`;
                    
//                     const existingIdx = acc.findIndex(item => item._groupKey === groupKey);
                    
//                     if (existingIdx > -1) {
//                       // Prioritize 'anniversary_post' over 'anniversary'
//                       if (feed.type === "anniversary_post") {
//                         acc[existingIdx] = { ...feed, _groupKey: groupKey };
//                       }
//                       return acc;
//                     }
                    
//                     acc.push({ ...feed, _groupKey: groupKey });
//                     return acc;
//                   }
//                   acc.push(feed);
//                   return acc;
//                 }, [])
//                 .map((feed) => (
 
//                   feed.type === "birthday" ? (
 
 
 
// <div
//   key={feed.id}
//   className="mb-8 w-full min-h-[362px] rounded-3xl overflow-hidden relative border border-white/10"
// >
//   {/* BACKGROUND */}
//   <div className="absolute inset-0 bg-gradient-to-r from-[#142a7c] via-[#1f3ea8] to-[#0f1c4d]"></div>

//   {/* CONTENT */}
//   <div className="relative z-10 flex h-full items-center justify-between px-12 pt-10 pb-6">
//     {/* LEFT TEXT */}
//     <div className="max-w-[320px]">
//       <p className="text-white/70 tracking-[6px] text-sm">Happy</p>
//       <h1 className="text-[56px] font-extrabold text-white leading-none">BIRTHDAY</h1>
//       <p className="text-white/80 text-sm mt-1 tracking-widest">YOUR MOMENT IS HERE</p>
//       <p className="mt-4 text-white/80 text-xs leading-relaxed">
//         Wishing you a fantastic day filled with happiness, laughter and unforgettable moments.
//       </p>
//       <h3 className="mt-5 text-cyan-300 text-xl font-semibold">
//         {feed.meta?.first_name} {feed.meta?.last_name}
//       </h3>

//       {/* DATE BADGE */}
//       <div className="mt-6 flex items-center gap-3">
//         <div className="w-[70px] h-[70px] rounded-full border border-white/40 flex flex-col items-center justify-center text-white">
//           <span className="text-xl font-bold">
//             {feed.event_date ? new Date(feed.event_date).getDate() : new Date().getDate()}
//           </span>
//           <span className="text-[10px]">
//             {feed.event_date
//               ? new Date(feed.event_date).toLocaleString('default', { month: 'short' }).toUpperCase()
//               : new Date().toLocaleString('default', { month: 'short' }).toUpperCase()
//             }
//           </span>
//         </div>
//         <span className="text-white/70 text-sm">Celebrate today 🎉</span>
//       </div>
//     </div>

//     {/* PROFILE IMAGE */}
//     <div className="relative">
//       <div className="absolute -inset-3 rounded-full bg-white/20 blur-lg"></div>
//       <img
//         src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
//         alt="profile"
//         className="relative w-[180px] h-[180px] object-cover rounded-full border-[10px] border-white/40 shadow-[0_15px_50px_rgba(0,0,0,0.8)]"
//       />
//     </div>
//   </div>

//   {/* ACTIONS & STATS */}
//   <div className="relative z-10 flex items-center justify-between px-12 py-5 border-t border-white/10 bg-black/20">
//     <div className="flex items-center gap-6">
//       <button
//         onClick={() => handleLike(feed)}
//         className={`flex items-center gap-2.5 transition-all duration-300 group
//         ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
//       >
//         <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
//         <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
//       </button>

//       <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
//         <MessageCircle size={22} />
//         <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
//       </div>
//     </div>

//     <button
//       onClick={() => handleShare(feed)}
//       className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
//     >
//       <Share2 size={18} />
//       <span className="text-xs font-bold uppercase tracking-wider">Share</span>
//     </button>
//   </div>

//   {/* COMMENTS SECTION */}
//   <div className="relative z-10 px-12 pb-8 pt-5 border-t border-white/10 bg-black/30">
//     {feed.comments?.length > 0 && (
//       <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
//         {feed.comments.map((c) => (
//           <div key={c.id} className="flex gap-3 group">
//             <img 
//               src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
//               className="w-8 h-8 rounded-full object-cover border border-white/10" 
//               alt="" 
//             />
//             <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
//               <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
//                 {c.user?.first_name} {c.user?.last_name}
//               </p>
//               <p className="text-[13px] text-white/80 leading-snug">
//                 {c.comment}
//               </p>
//             </div>
//           </div>
//         ))}
//       </div>
//     )}
//     <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
//   </div>
// </div>
 
//                     ) : (feed.type === "anniversary" || feed.type === "anniversary_post") ? (
//                       <div
//                         key={feed.id}
//                         className="mb-10 w-full min-h-[400px] rounded-[40px] overflow-hidden relative border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.7)] group"
//                       >
//                         {/* PREMIUM BACKGROUND */}
//                         <div className="absolute inset-0 bg-[#0f0c29]">
//                           <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] bg-purple-600/30 rounded-full blur-[100px] animate-pulse"></div>
//                           <div className="absolute bottom-[-10%] left-[-10%] w-[350px] h-[350px] bg-indigo-600/20 rounded-full blur-[120px]"></div>
//                           <div className="absolute inset-0 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f]"></div>
//                         </div>

//                         {/* DECORATIVE ELEMENTS */}
//                         <div className="absolute inset-0 overflow-hidden pointer-events-none">
//                            <div className="absolute top-10 left-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
//                            <div className="absolute bottom-10 right-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
//                            <Sparkles className="absolute top-10 right-10 text-yellow-400/30 animate-twinkle" size={40} />
//                         </div>

//                         {/* CONTENT */}
//                         <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-10">
                          
//                           <div className="mb-6 px-5 py-1.5 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
//                             <p className="text-indigo-300 tracking-[8px] text-[10px] font-black uppercase">
//                               {feed.meta?.day_type === 'tomorrow' ? "Tomorrow's Anniversary" : "Work Anniversary"}
//                             </p>
//                           </div>
                          
//                           <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8 tracking-tighter">
//                             {feed.meta?.day_type === 'tomorrow' ? (
//                               <>JOINING US<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOMORROW</span></>
//                             ) : (feed.meta?.users?.length > 1 || feed.meta?.is_combined) ? (
//                               <>CELEBRATING<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOGETHER</span></>
//                             ) : (
//                               <>YEARS OF<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">EXCELLENCE</span></>
//                             )}
//                           </h1>

//                           {/* AVATARS GROUP */}
//                           <div className="flex -space-x-8 mb-10 items-center justify-center translate-x-4">
//                             {(feed.meta?.users || []).map((emp, idx) => (
//                               <div 
//                                 key={emp.id} 
//                                 className="relative transition-all duration-500 hover:scale-110 hover:-translate-y-2 hover:z-30"
//                                 style={{ zIndex: 10 + idx }}
//                               >
//                                 <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40 group-hover:opacity-70 transition-opacity"></div>
//                                 <img
//                                   src={`${IMAGE_BASE_URL}${emp.profile_pic || feed.publisher_profile_pic || '/default-avatar.png'}`}
//                                   className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
//                                   alt={emp.first_name}
//                                 />
//                                 <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-lg border border-white/20 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
//                                   {emp.first_name} {emp.last_name}
//                                 </div>
//                               </div>
//                             ))}
                            
//                             {/* If no users in meta (old format or fallback) */}
//                             {(!feed.meta?.users || feed.meta.users.length === 0) && (
//                                <div className="relative">
//                                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40"></div>
//                                  <img
//                                    src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
//                                    className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
//                                    alt=""
//                                  />
//                                </div>
//                             )}
//                           </div>

//                           <div className="max-w-[550px] space-y-4">
//                             <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
//                               {feed.meta?.users?.length > 1 
//                                 ? `${feed.meta.users.map(e => e.first_name).join(' & ')}`
//                                 : `${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}`
//                               }
//                             </h2>
                            
//                             <div className="inline-flex items-center gap-3 px-6 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl border border-white/10">
//                                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
//                                <p className="text-blue-200 text-lg font-medium italic">
//                                  Completed {feed.meta?.users?.[0]?.years_completed || feed.meta?.years_completed || 1} Year milestone
//                                </p>
//                             </div>

//                             <p className="text-white/50 text-sm leading-relaxed font-light px-4">
//                               {feed.meta?.message || "Today marks a special milestone in your journey with us. Your dedication and passion have been truly inspiring!"}
//                             </p>
//                           </div>

//                           {/* DATE BADGE */}
//                           <div className="mt-12 flex flex-col items-center gap-2">
//                              <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
//                              <p className="text-[11px] text-white/30 font-bold tracking-[4px] uppercase">
//                                {moment(feed.event_date).format("MMMM YYYY")}
//                              </p>
//                           </div>
//                         </div>

//                         {/* ACTIONS & STATS */}
//                         <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
//                           <div className="flex items-center gap-6">
//                             <button
//                               onClick={() => handleLike(feed)}
//                               className={`flex items-center gap-2.5 transition-all duration-300 group
//                               ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
//                             >
//                               <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
//                               <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
//                             </button>

//                             <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
//                               <MessageCircle size={22} />
//                               <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
//                             </div>
//                           </div>

//                           <button
//                             onClick={() => handleShare(feed)}
//                             className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
//                           >
//                             <Share2 size={18} />
//                             <span className="text-xs font-bold uppercase tracking-wider">Share</span>
//                           </button>
//                         </div>

//                         {/* COMMENTS SECTION */}
//                         <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
//                           {feed.comments?.length > 0 && (
//                             <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
//                               {feed.comments.map((c) => (
//                                 <div key={c.id} className="flex gap-3 group">
//                                   <img 
//                                     src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
//                                     className="w-8 h-8 rounded-full object-cover border border-white/10" 
//                                     alt="" 
//                                   />
//                                   <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
//                                     <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
//                                       {c.user?.first_name} {c.user?.last_name}
//                                     </p>
//                                     <p className="text-[13px] text-white/80 leading-snug">
//                                       {c.comment}
//                                     </p>
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           )}
//                           <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
//                         </div>
//                       </div>
//                     ) : feed.type === "new_joiner" ? (
//                       (() => {
//                         const meta = typeof feed.meta === 'string' ? JSON.parse(feed.meta) : feed.meta;
//                         const isFemale = (meta?.gender || '').toLowerCase() === 'female';
                       
//                         return (
//                           /* 🚀 NEW JOINER BANNER (ULTRA PREMIUM) */
// <div
//   key={feed.id}
//   className="mb-8 w-full min-h-[320px] rounded-3xl overflow-hidden relative border border-white/5"
// >
//   {/* BACKGROUND */}
//   <div className="absolute inset-0 bg-[#0f0c29] overflow-hidden">
//     <div className={`absolute top-0 right-0 w-[400px] h-[400px] ${isFemale ? 'bg-pink-600/30' : 'bg-purple-600/30'} rounded-full blur-[100px] -mr-40 -mt-40 animate-pulse`}></div>
//     <div className={`absolute bottom-0 left-0 w-[350px] h-[350px] ${isFemale ? 'bg-purple-600/20' : 'bg-blue-600/20'} rounded-full blur-[90px] -ml-30 -mb-30`}></div>
//     <div className={`absolute inset-0 bg-gradient-to-br ${isFemale ? 'from-[#4a1a3a] via-[#1a0c2f] to-[#0a081f]' : 'from-[#1e1a5a] via-[#100c2f] to-[#0a081f]'}`}></div>
//   </div>
  
//   {/* DECOR */}
//   <div className="absolute inset-0 pointer-events-none">
//     <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full opacity-40 animate-ping"></div>
//     <div className={`absolute bottom-20 right-20 w-3 h-3 ${isFemale ? 'bg-pink-400' : 'bg-cyan-400'} rounded-full opacity-30 animate-pulse`}></div>
//   </div>
  
//   {/* CONTENT */}
//   <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-12 pb-6">
  
//     {/* TAG */}
//     <span className={`mb-4 px-4 py-1 bg-white/10 rounded-full text-[11px] uppercase tracking-[3px] font-bold ${isFemale ? 'text-pink-400' : 'text-cyan-400'} border border-white/10 backdrop-blur-md`}>
//       ✨ New Team Member
//     </span>
  
//     {/* HEADING */}
//     <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight tracking-tight uppercase">
//       WELCOME
//       <br />
//       <span className={`text-transparent bg-clip-text bg-gradient-to-r ${isFemale ? 'from-white via-pink-300 to-purple-400' : 'from-white via-cyan-300 to-blue-400'}`}>
//         TO THE TEAM
//       </span>
//     </h1>
  
//     {/* NAME + ROLE */}
//     <div className="mt-6">
//       <h2 className="text-2xl md:text-3xl font-bold text-white">
//         {meta?.first_name} {meta?.last_name}
//       </h2>
  
//       <p className="mt-2 text-lg text-white/70 italic">
//         {meta?.job_title || "Innovative Team Member"}
//       </p>
//     </div>
//   </div>

//   {/* ACTIONS & STATS */}
//   <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
//     <div className="flex items-center gap-6">
//       <button
//         onClick={() => handleLike(feed)}
//         className={`flex items-center gap-2.5 transition-all duration-300 group
//         ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
//       >
//         <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
//         <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
//       </button>

//       <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
//         <MessageCircle size={22} />
//         <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
//       </div>
//     </div>

//     <button
//       onClick={() => handleShare(feed)}
//       className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
//     >
//       <Share2 size={18} />
//       <span className="text-xs font-bold uppercase tracking-wider">Share</span>
//     </button>
//   </div>

//   {/* COMMENTS SECTION */}
//   <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
//     {feed.comments?.length > 0 && (
//       <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
//         {feed.comments.map((c) => (
//           <div key={c.id} className="flex gap-3 group">
//             <img 
//               src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
//               className="w-8 h-8 rounded-full object-cover border border-white/10" 
//               alt="" 
//             />
//             <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
//               <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
//                 {c.user?.first_name} {c.user?.last_name}
//               </p>
//               <p className="text-[13px] text-white/80 leading-snug">
//                 {c.comment}
//               </p>
//             </div>
//           </div>
//         ))}
//       </div>
//     )}
//     <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
//   </div>
// </div>
//                         );
//                       })()
//                     ) : (
 
//                     /* NORMAL POST */
//                     <div
//                       key={feed.id}
//                       className="
//                       mb-10 rounded-[32px] bg-gradient-to-br from-[#1e174a]/80 to-[#0f0c29]/90 backdrop-blur-2xl
//                       border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.5)]
//                       overflow-hidden transition-all duration-300 hover:shadow-[0_35px_80px_rgba(0,0,0,0.7)] hover:translate-y-[-4px]
//                       "
//                     >
//                       {/* HEADER */}
//                       <div className="flex items-center justify-between px-7 py-6">
//                         <div className="flex items-center gap-4">
//                           <div className="relative">
//                             <img
//                               src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
//                               className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/50 shadow-lg"
//                               alt=""
//                             />
//                             <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#1e174a] rounded-full"></div>
//                           </div>
//                           <div>
//                             <p className="font-bold text-white text-[15px] tracking-wide">
//                               {feed.publisher_name}
//                             </p>
//                             <p className="text-[11px] text-white/40 font-medium uppercase tracking-tighter">
//                                {moment(feed.created_at).format("DD MMM YYYY")}
//                             </p>
//                           </div>
//                         </div>

//                         <button
//                           onClick={() => handleDelete(feed.id)}
//                           className="p-2 rounded-full hover:bg-white/5 text-white/30 hover:text-red-400 transition-colors"
//                         >
//                           <Trash2 size={18} />
//                         </button>
//                       </div>

//                       {/* MESSAGE */}
//                       {feed.meta?.message && (
//                         <div className="px-7 pb-4">
//                           <div className="text-[16px] text-white/90 leading-relaxed break-words font-light">
//                             {feed.meta.message.split("\n").map((line, i) => (
//                               <span key={i}>
//                                 {line}
//                                 <br />
//                               </span>
//                             ))}
//                           </div>
//                         </div>
//                       )}

//                       {/* IMAGE */}
//                       {feed.image && (
//                         <div className="px-5 pb-6">
//                           <div className="rounded-[24px] overflow-hidden bg-black/40 border border-white/5 shadow-inner">
//                             <img
//                               src={`${IMAGE_BASE_URL}/${feed.image}`}
//                               className="w-full max-h-[600px] object-contain mx-auto transition-transform duration-700 hover:scale-[1.02]"
//                               alt=""
//                             />
//                           </div>
//                         </div>
//                       )}

//                       {/* ACTIONS & STATS */}
//                       <div className="flex items-center justify-between px-7 py-5 border-t border-white/5 bg-white/[0.02]">
//                         <div className="flex items-center gap-6">
//                           <button
//                             onClick={() => handleLike(feed)}
//                             className={`flex items-center gap-2.5 transition-all duration-300 group
//                             ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
//                           >
//                             <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
//                             <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
//                           </button>

//                           <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
//                             <MessageCircle size={22} />
//                             <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
//                           </div>
//                         </div>

//                         <button
//                           onClick={() => handleShare(feed)}
//                           className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
//                         >
//                           <Share2 size={18} />
//                           <span className="text-xs font-bold uppercase tracking-wider">Share</span>
//                         </button>
//                       </div>

//                       {/* COMMENTS SECTION */}
//                       <div className="px-7 pb-6 pt-5 border-t border-white/5 bg-black/10">
//                         {feed.comments?.length > 0 && (
//                           <div className="space-y-4 mb-5">
//                             {feed.comments.map((c) => (
//                               <div key={c.id} className="flex gap-3 group">
//                                 <img 
//                                   src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
//                                   className="w-8 h-8 rounded-full object-cover border border-white/10" 
//                                   alt="" 
//                                 />
//                                 <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
//                                   <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
//                                     {c.user?.first_name} {c.user?.last_name}
//                                   </p>
//                                   <p className="text-[13px] text-white/80 leading-snug">
//                                     {c.comment}
//                                   </p>
//                                 </div>
//                               </div>
//                             ))}
//                           </div>
//                         )}
//                         <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
//                       </div>
//                     </div>
//                   )
 
//                 ))
 
//             )}
 
//           </div>
 
//         </div>
 
//       </div>
 
//       <CreatePostModal
//         showModal={showModal}
//         setShowModal={setShowModal}
//         onPostCreated={loadFeeds}
//       />
 
//     </div>
//   );
// }
 
 
// /* COMMENT BOX */
// function CommentBox({ onSubmit }) {
//   const [text, setText] = useState("");

//   const handleSend = () => {
//     if (!text.trim()) return;
//     onSubmit(text);
//     setText("");
//   };

//   return (
//     <div className="flex gap-3 mt-4">
//       <div className="flex-1 relative group">
//         <input
//           value={text}
//           onChange={(e) => setText(e.target.value)}
//           onKeyDown={(e) => e.key === "Enter" && handleSend()}
//           placeholder="Write a comment..."
//           className="w-full bg-white/5 border border-white/10 px-5 py-3 rounded-2xl text-[14px] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
//         />
//       </div>

//       <button
//         onClick={handleSend}
//         disabled={!text.trim()}
//         className="bg-cyan-600 p-3 rounded-2xl text-white hover:bg-cyan-500 disabled:opacity-40 disabled:hover:bg-cyan-600 transition-all active:scale-90 flex items-center justify-center shadow-lg shadow-cyan-900/20"
//       >
//         <Send size={20} />
//       </button>
//     </div>
//   );
// }
